import { useState, useEffect } from "react";

import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Card } from "react-bootstrap";
import { useNavigate, useLocation, Link } from "react-router-dom";

import { handleSuccess, handleError } from "@/components/LoginStatusModal/LoginStatusModal";
import { ALL_ROLES } from "@/constants/pagePermissionDefaults";

import "./LoginPage.scss";

import niraLoginBg from "../../assets/NIRALogInBgLogo.webp";
import niraLogoLong from "../../assets/NIRALogo_long.webp";
import { useAuth } from "../../context/AuthContext";
import { handleOIDCCallback, initiateMicrosoftLogin } from "../../services/auth";
import { logger } from "../../utils/logger";


function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [, setError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { login, errorMessage } = useAuth();

  // Handle OIDC callback and error parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");
    const state = urlParams.get("state");
    const error = urlParams.get("error");
    const authStatus = urlParams.get("auth");

    // Handle OAuth 2FA pending state
    if (authStatus === "pending") {
      // User needs to verify 2FA after OAuth
      navigate("/2fa-verify");
      return;
    }

    if (error === "insufficient_permissions") {
      handleError("Invalid credentials. Please check your email and password.");
      return;
    }

    if (error) {
      logger.error("OIDC error", { error, context: "useEffect-OIDC" });
      handleError(`Authentication failed: ${error}`);
      return;
    }

    if (code && state) {
      handleOIDCCallback(code, state).then((result) => {
        if (result.success) {
          // Check if user has valid role after Microsoft login
          const checkRoleAndNavigate = async () => {
            try {
              const { checkAuthFunction } = await import("../../services/auth");
              const { getUserOrganization } = await import("../../services/roles");
              const response = await checkAuthFunction();
              if (response?.user) {
                const roleResponse = await getUserOrganization(response.user.id);
                const userRole = roleResponse.role || "";
                if ((ALL_ROLES as readonly string[]).includes(userRole)) {
                  handleSuccess();
                  setTimeout(() => {
                    // Redirect CxO users to executive dashboard (summary page)
                    if (userRole === "CxO") {
                      navigate("/executive-dashboard");
                    } else {
                      navigate("/main");
                    }
                  }, 100);
                } else {
                  handleError("Invalid credentials. Please check your email and password.");
                }
              } else {
                handleError("Invalid credentials. Please check your email and password.");
              }
            } catch {
              handleError("Invalid credentials. Please check your email and password.");
            }
          };
          checkRoleAndNavigate();
        } else {
          handleError(result.message);
        }
      });
    }
  }, [location, navigate]);

  const handleLogin = async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await login(email, password);

      // Check if 2FA is required
      if (result.requires2FA) {
        // Redirect to 2FA verification page
        navigate("/2fa-verify");
        return;
      }

      if (result.success) {
        // Small delay to ensure state is properly updated
        setTimeout(() => {
          handleSuccess();
          setTimeout(async () => {
            // Get user role to determine redirect destination
            try {
              const { checkAuthFunction } = await import("../../services/auth");
              const { getUserOrganization } = await import("../../services/roles");
              const response = await checkAuthFunction();
              if (response?.user) {
                const roleResponse = await getUserOrganization(response.user.id);
                const userRole = roleResponse.role || "";

                // Redirect CxO users to executive dashboard (summary page)
                if (userRole === "CxO") {
                  navigate("/executive-dashboard");
                } else {
                  navigate("/main");
                }
              } else {
                navigate("/main");
              }
            } catch (error) {
              logger.error("Error getting user role for redirect", {
                error,
                context: "checkRoleAndNavigate",
              });
              navigate("/main");
            }
          }, 100);
        }, 50);
      } else {
        const message =
          errorMessage || "Invalid credentials. Please check your email and password.";
        setError(message);
        handleError(message);
      }
    } catch (err) {
      logger.error("Login page error", { error: err, context: "handleOIDCCallback" });
      const error = err as Error;
      setError(error.message || "An unexpected error occurred");
      handleError(error.message || "An unexpected error occurred");
    } finally {
      setLoading(false);
    }
  };

  const _handleMicrosoftLogin = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    initiateMicrosoftLogin();
  };

  return (
    <div>
      <section className="login-input-container">
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector"></div>
        </div>
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector-1"></div>
        </div>
        <div className="geometric-vector-wrapper">
          <div className="geometric-vector-2"></div>
        </div>

        <div className="depth-effect-overlay"></div>

        <img src={niraLoginBg} alt="NIRA Background Logo" className="background-logo" />
        {/* LOGIN CARD */}
        <div className="card-container">
          <Card className="login-card">
            <section className="d-flex flex-column justify-content-center align-items-center">
              <img src={niraLogoLong} alt="NIRA Logo" className="login-logo mt-4" />
              <div className="powered-by-text">Powered by: Diem360</div>
              <p className="p-small">Please enter your credentials.</p>
            </section>
            <Card.Body>
              <form className="flex-column align-items-center justify-content-center login-form-fullwidth">
                <div className="inputs floating-label">
                  <input
                    id="mail"
                    type="text"
                    autoComplete="off"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder=" "
                  />
                  <label htmlFor="mail">Email</label>
                </div>
                <div className="inputs floating-label password-input-wrapper">
                  <input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    autoComplete="off"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder=" "
                  />
                  <label htmlFor="password">Password</label>
                  <button
                    className="show-password-btn"
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FontAwesomeIcon icon={faEyeSlash} />
                    ) : (
                      <FontAwesomeIcon icon={faEye} />
                    )}
                  </button>
                </div>
                <div className="forgot-password-wrapper">
                  <Link to="/forgot-password" className="forgot-password-link">
                    Forgot Password?
                  </Link>
                </div>
                <div className="button-group">
                  <button
                    className="enter mt-3"
                    onClick={(e) => {
                      e.preventDefault();
                      handleLogin();
                    }}
                    disabled={loading}
                  >
                    {loading ? "Loading..." : "Let's get started!"}
                  </button>
                  {/* Sign in with Microsoft — parked, not in use
                  <div className="separator-fullwidth">
                    <span> or </span>
                  </div>
                  <div className="sso-login-gradient-wrapper">
                    <button
                      type="button"
                      className="sso-login-fullwidth"
                      disabled={loading}
                      onClick={handleMicrosoftLogin}
                    >
                      Sign in with Microsoft
                    </button>
                  </div>
                  */}
                </div>
              </form>
            </Card.Body>
          </Card>
        </div>
      </section>
    </div>
  );
}

export default LoginPage;
